import React, { ReactElement, memo } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'
import { Link } from 'react-router-dom'

import BootstrapModalLayout from '../../../ui/BootstrapModalLayout'
import useText from '../../../common/hooks/useText'
import {
    useApiCustomerDeleteMutation,
    ApiCustomer,
    useApiCustomerGetChargePointsQuery,
} from '../../../common/redux/api/web-api'
import Text from '../../../ui/Text'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import { Button } from '../../../common/components/Button'
import useKeyboardHandle from '../../../common/hooks/useKeyboardHandle'

export type DeleteApiCustomerModalProps = {
    onCancel: () => void
    isOpened?: boolean
    apiCustomer: ApiCustomer
    className?: ClassName
    style?: React.CSSProperties
}

const ModalSubHeading = styled.div`
    font-weight: 300;
    font-size: 14px;
    color: ${({ theme }) => theme.font.midDarkColor};
    margin: 6px 0px 7px 0px;
`
const ConfirmationText = styled.div`
    font-weight: 400;
    font-size: 16px;
    color: ${({ theme }) => theme.font.darkestColor};
    margin-top: 20px;
    margin-bottom: 20px;
`
const ModalSubText = styled.div`
    font-weight: 400;
    font-size: 16px;
    color: ${({ theme }) => theme.font.darkestColor};
    margin-bottom: 20px;
`

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    overflow-x: hidden;
`

const ModalListContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
`

const ModalListItem = styled.a`
    & {
        color: ${({ theme }) => theme.font.darkestColor};
        text-decoration: underline;
        font-size: 14px;
        margin: 5px;
    }

    &:hover {
        color: ${({ theme }) => theme.font.successColor};
        cursor: pointer;
    }

    &:active {
        color: ${({ theme }) => theme.font.midDarkColor};
    }
`

export const DeleteApiCustomerModal = memo(function DeleteExternalServerModal({
    className,
    apiCustomer,
    onCancel,
    isOpened,
    style,
}: DeleteApiCustomerModalProps): ReactElement | null {
    const text = useText()
    const [removeCustomer] = useApiCustomerDeleteMutation()

    const {
        data: chargePoints,
        isFetching: dataLoading,
        isLoading,
    } = useApiCustomerGetChargePointsQuery({ apiCustomerId: apiCustomer.id ?? 0 }, { skip: !apiCustomer.id })

    const handleRemoveApiCustomer = async () => {
        if (apiCustomer && apiCustomer.id) {
            await removeCustomer({ apiCustomerId: apiCustomer.id })
                .unwrap()
                .then(() => {
                    onCancel()
                    notifySuccess(text('msgDeleteApiCustomerSuccess'))
                })
                .catch(() => {
                    onCancel()
                    notifyError(text('msgDeleteApiCustomerError'))
                })
        }
    }

    useKeyboardHandle({
        onEnterPress: () => {
            if (!chargePoints || chargePoints?.length === 0) handleRemoveApiCustomer()
        },
        onEscapePress: onCancel,
    })

    return isOpened ? (
        <BootstrapModalLayout
            className={cn(className)}
            style={style}
            title={text('lblCustomerDeleteModal')}
            titleClassName="modal-header text-center"
            size={'md'}
            data-testid="delete-modal"
        >
            {!isLoading && (
                <>
                    {chargePoints && chargePoints?.length > 0 ? (
                        <ModalContent data-testid="chargers-list-to-server">
                            <ModalSubText data-testid="chargers-list-to-server-text">
                                <Text code="msgChargersConnectedToApiCustomer" />
                            </ModalSubText>
                            <ModalSubHeading data-testid="chargers-list">
                                <Text code="lblChargersffected" />
                                {dataLoading && (
                                    <span data-testid="chargers-list-loading" className="fa fa-spinner fa-pulse"></span>
                                )}
                            </ModalSubHeading>
                            <ModalListContainer>
                                {chargePoints?.map((chargePoint) => (
                                    <ModalListItem
                                        as={Link}
                                        key={chargePoint}
                                        to={`/chargepoints/${chargePoint}`}
                                        target="_blank"
                                        data-testid={`chargePoints-${chargePoint}`}
                                    >
                                        {chargePoint}
                                    </ModalListItem>
                                ))}
                            </ModalListContainer>
                        </ModalContent>
                    ) : (
                        <>
                            <ConfirmationText data-testid="confirmation-text">
                                <Text code="lblProceedConfirmation" />
                                {isLoading && <span className="fa fa-spinner fa-pulse"></span>}
                            </ConfirmationText>
                            <ModalContent data-testid="api-customer-name-url">
                                <ModalSubHeading data-testid="api-name-heading">
                                    <Text code="lblApiName" />
                                </ModalSubHeading>
                                <ModalSubText data-testid="api-name-text">{apiCustomer.name}</ModalSubText>
                                <ModalSubHeading data-testid="callback-text-url-heading">
                                    <Text code="lblApiCallbackUrl" />
                                </ModalSubHeading>
                                <ModalSubText data-testid="callback-text-url-text">
                                    {apiCustomer.callbackBaseUrl ?? '---'}
                                </ModalSubText>
                            </ModalContent>
                        </>
                    )}
                </>
            )}
            <div className="modal-footer margin-top-med" data-testid="modal-footer-buttons">
                <Button
                    primary={false}
                    label={text('btnCancel')}
                    type="button"
                    data-testid="modal-cancel-button"
                    minWidth="150px"
                    onClick={onCancel}
                />
                <Button
                    primary
                    disabled={chargePoints && chargePoints?.length > 0}
                    type="button"
                    label={text('btnYes')}
                    minWidth="150px"
                    data-testid="modal-submit-button"
                    onClick={handleRemoveApiCustomer}
                />
            </div>
        </BootstrapModalLayout>
    ) : null
})
